import { z } from "zod";
import { EntityIdSchema, EntityTypeSchema, LocalIdSchema } from "./common.ts";
import { BoundedFilterSchema } from "./filter.ts";
import { FieldValueDataSchema } from "./fieldValue.ts";
import { StatusIdSchema } from "./status.ts";
import { ScopeStringSchema } from "./trackQuery.ts";

/**
 * Bulk targets: either an explicit id list, or a scope/filter/q that the server
 * resolves. Select-all-filtered uses the second form so the client never ships
 * 3,800 ids.
 */
export const BulkTargetSchema = z
  .object({
    entity_type: EntityTypeSchema.default("track"),
    entity_ids: z.array(EntityIdSchema).min(1).max(10_000).optional(),
    scope: ScopeStringSchema.optional(),
    filter: BoundedFilterSchema.optional(),
    q: z.string().trim().max(200).optional(),
  })
  .superRefine((value, ctx) => {
    const hasIds = (value.entity_ids?.length ?? 0) > 0;
    const hasScope = value.scope !== undefined;
    if (!hasIds && !hasScope) {
      ctx.addIssue({
        code: "custom",
        message: "Provide entity_ids or a scope (optionally with filter/q)",
      });
    }
  });

export type BulkTarget = z.infer<typeof BulkTargetSchema>;

export const BulkStatusSchema = BulkTargetSchema.extend({
  status: StatusIdSchema.optional(),
  follow_up: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .nullable()
    .optional(),
  drive_url: z.string().url().max(2000).nullable().optional(),
}).superRefine((value, ctx) => {
  if (
    value.status === undefined &&
    value.follow_up === undefined &&
    value.drive_url === undefined
  ) {
    ctx.addIssue({ code: "custom", message: "Provide status, follow_up, or drive_url" });
  }
});

export type BulkStatus = z.infer<typeof BulkStatusSchema>;

export const BulkValuesSchema = BulkTargetSchema.extend({
  values: z.record(z.string(), FieldValueDataSchema).refine((v) => Object.keys(v).length > 0, {
    message: "Provide at least one field value",
  }),
});

export type BulkValues = z.infer<typeof BulkValuesSchema>;

export const BulkDeleteValuesSchema = BulkTargetSchema.extend({
  field_keys: z.array(z.string().min(1)).min(1).max(50),
});

export type BulkDeleteValues = z.infer<typeof BulkDeleteValuesSchema>;

export const BulkGenresSchema = BulkTargetSchema.extend({
  add: z.array(LocalIdSchema).max(200).optional(),
  remove: z.array(LocalIdSchema).max(200).optional(),
}).superRefine((value, ctx) => {
  if ((value.add?.length ?? 0) + (value.remove?.length ?? 0) === 0) {
    ctx.addIssue({ code: "custom", message: "Provide genres to add or remove" });
  }
});

export type BulkGenres = z.infer<typeof BulkGenresSchema>;

export const BulkPlaylistSchema = z.object({
  local_playlist_id: LocalIdSchema,
  action: z.enum(["add", "remove"]),
  track_ids: z.array(EntityIdSchema).min(1).max(10_000).optional(),
  scope: ScopeStringSchema.optional(),
  filter: BoundedFilterSchema.optional(),
  q: z.string().trim().max(200).optional(),
}).superRefine((value, ctx) => {
  if (!(value.track_ids?.length) && value.scope === undefined) {
    ctx.addIssue({ code: "custom", message: "Provide track_ids or a scope" });
  }
});

export type BulkPlaylist = z.infer<typeof BulkPlaylistSchema>;

export const BulkResultSchema = z.object({
  updated: z.number(),
  undo_id: z.number().int(),
  label: z.string(),
});

export type BulkResult = z.infer<typeof BulkResultSchema>;
